
    import __i18nConfig from '@next-translate-root/i18n'
    import __appWithI18n from 'next-translate/appWithI18n'
    import '../styles/datepicker.scss';
import '../styles/global.scss';
import '../styles/reset.scss';
// above styles need to be imported before everything else
import * as Sentry from '@sentry/nextjs';
import { Navigation as AdminNavigation } from 'components/admin/layout/Navigation';
import { OnboardingContextProvider } from 'components/admin/onboarding';
import { AuthContextProvider } from 'components/auth';
import { ImpersonationProvider } from 'components/auth/impersonation';
import { HistoryContextProvider } from 'components/history';
import { Navigation as InternalNavigation } from 'components/internal/layout/Navigation';
import { Toast } from 'components/ui/Alert';
import { ErrorPage } from 'components/util/ErrorPage';
import { WhiteLabelContextProvider } from 'components/white-label/WhiteLabelContextProvider';
import 'core-js/full/promise/with-resolvers';
import { MotionConfig } from 'framer-motion';
import { useSoftLogin } from 'lib/auth';
import { ADMIN_PAGES } from 'lib/datasets/pages';
import { getServerSession, setServerContext } from 'lib/serverContext';
import { IPageProps } from 'lib/types/page';
import _ from 'lodash';
import NextAdapterPages from 'next-query-params/pages';
import type { AppContext, AppProps } from 'next/app';
import App from 'next/app';
import type { ErrorProps } from 'next/error';
import Head from 'next/head';
import { useRouter } from 'next/router';
import { useEffect } from 'react';
import { SWRConfig } from 'swr';
import { QueryParamProvider } from 'use-query-params';
type EthenaAppProps = AppProps<IPageProps> & {
    err: ErrorProps;
    headers: {
        host: string;
    };
};
// Ideally, the Navigation component should live within the Layout component.
// Since nextjs partially behaves like a single page application, any time we
// would navigate from one page to another the navigation menu would close and
// then pop back open.
//
// In order to fix this we had to raise the navigation component outside of the
// Layout component and render it conditionally depending on whether the user
// is looking on an admin route.
const EthenaApp = ({ Component, err, headers, pageProps }: EthenaAppProps) => {
    const { pathname } = useRouter();
    useEffect(() => {
        // Identify the authenticated user.
        if (pageProps.session?.user) {
            Sentry.setUser({
                id: pageProps.session.user.id,
                email: pageProps.session.user.primaryEmail,
                company_id: pageProps.session.company?.id,
            });
        }
    }, [pageProps]);
    useSoftLogin(pageProps.useSoftLogin);
    if (pageProps.error) {
        return (<WhiteLabelContextProvider host={headers?.host} organization={pageProps.session?.company.organization}>
        <ErrorPage message={pageProps.message}/>
      </WhiteLabelContextProvider>);
    }
    const isAdminNavigationVisible = !pageProps.hideAdminNavigation &&
        _.some(ADMIN_PAGES, (x) => _.startsWith(pathname, x.path));
    const renderNavigation = () => {
        if (_.includes(pathname, '/internal')) {
            return <InternalNavigation />;
        }
        if (isAdminNavigationVisible) {
            return (<OnboardingContextProvider>
          <AdminNavigation />
        </OnboardingContextProvider>);
        }
    };
    return (<>
      <Head>
        <meta name="viewport" content="width=device-width, maximum-scale=2"/>
      </Head>
      <AuthContextProvider session={pageProps.session}>
        <WhiteLabelContextProvider host={headers?.host} organization={pageProps.session?.company.organization}>
          <HistoryContextProvider>
            <SWRConfig value={{ revalidateOnFocus: false }}>
              <QueryParamProvider adapter={NextAdapterPages}>
                <MotionConfig reducedMotion="user">
                  <Toast>
                    <ImpersonationProvider>
                      {renderNavigation()}
                      <Component {...pageProps} err={err}/>
                    </ImpersonationProvider>
                  </Toast>
                </MotionConfig>
              </QueryParamProvider>
            </SWRConfig>
          </HistoryContextProvider>
        </WhiteLabelContextProvider>
      </AuthContextProvider>
    </>);
};
EthenaApp.getInitialProps = async (appContext: AppContext) => {
    const { ctx } = appContext;
    const host = ctx.req?.headers['x-forwarded-host'] || window.location.host;
    const serverSession = getServerSession();
    const getErrorProps = (e: Error) => ({
        pageProps: { error: true, message: e.message },
        headers: { host },
    });
    const getPageProps = async () => {
        const appProps = await App.getInitialProps(appContext);
        return {
            ...appProps,
            headers: { host },
        };
    };
    if (serverSession) {
        /**
         * We store the request context in a 'session' on the server so we can access it in `fetch.ts` and `redirect.ts`.
         * It contains the request headers, query params, and other useful information that wouldn't normally be available
         * unless it's captured here.
         */
        return serverSession.runAndReturn(async () => {
            setServerContext(ctx);
            console.log('[next/server]: ', ctx.asPath);
            try {
                return getPageProps();
            }
            catch (e) {
                return getErrorProps(e);
            }
        });
    }
    try {
        return getPageProps();
    }
    catch (e) {
        return getErrorProps(e);
    }
};
const __Next_Translate__Page__197f5f3ec04__ = EthenaApp;

    export default __appWithI18n(__Next_Translate__Page__197f5f3ec04__, {
      ...__i18nConfig,
      isLoader: true,
      skipInitialProps: false,
      
    });
  